import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import { Box, Typography, CircularProgress, Backdrop, ToggleButton, ToggleButtonGroup, Paper } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import { dataServices, helperServices, configServices } from '../services';

// Import marker images
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Color helpers for refstatus_final
const getStatusColor = (status) => {
  const GREY = '#9e9e9e';
  if (status === null || status === undefined) return GREY; // grey for null
  const s = String(status).trim().toLowerCase();
  // grey for unknown-like values
  if (!s || s === 'unknown' || s === 'null' || s === 'na' || s === 'n/a' || s === 'none') return GREY;
  if (s === 'stressed') return '#e53935'; // red
  if (s === 'reference') return '#2e7d32'; // green
  if (s === 'intermediate') return '#fbc02d'; // yellow
  return GREY; // default to grey for anything else
};

// Color helpers for flowstatus_final
const getFlowStatusColor = (status) => {
  const GREY = '#969696';
  if (status === null || status === undefined) return GREY; // grey for null
  const s = String(status).trim().toUpperCase(); // Use uppercase since values are E, I, NP, etc.
  // grey for unknown-like values
  if (!s || s === 'UNKNOWN' || s === 'NULL' || s === 'NA' || s === 'N/A' || s === 'NONE' || s === 'U') return GREY;
  
  // Use actual flowstatus_final values from the database
  if (s === 'P') return '#2c7bb6'; // blue - Perennial
  if (s === 'NP') return '#5e3c99'; // purple - Non-Perennial
  if (s === 'I') return '#abd9e9'; // light blue - Intermittent  
  if (s === 'RFI') return '#b2df8a'; // green
  if (s === 'SFI') return '#fdae61'; // orange - Seldom Flowing Intermittent
  if (s === 'E') return '#d7191c'; // red - Ephemeral
  
  // Log unknown values so we can see what's actually in the data
  return GREY; // default to grey for anything else
};

// Custom marker icon for unselected stations with status-based color
const createStatusIcon = (status, statusType = 'reference') => {
  const color = statusType === 'reference' ? getStatusColor(status) : getFlowStatusColor(status);
  const size = 16; // Normal size
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        opacity: 0.9;
      "></div>
    `,
    iconSize: [size + 4, size + 4],
    iconAnchor: [(size + 4) / 2, (size + 4) / 2],
    popupAnchor: [0, -(size / 2)],
  });
};

// Custom marker icon for selected station with pulsing effect using status color
const createSelectedStatusIcon = (status, statusType = 'reference') => {
  const color = statusType === 'reference' ? getStatusColor(status) : getFlowStatusColor(status);
  const size = 24; // Bigger size
  return L.divIcon({
    className: 'selected-marker-pulse',
    html: `
      <div style="position: relative;">
        <div class="pulse-ring" style="
          position: absolute;
          width: ${size + 20}px;
          height: ${size + 20}px;
          border-radius: 50%;
          border: 3px solid ${color};
          opacity: 0;
          animation: pulse 2s ease-out infinite;
          top: -10px;
          left: -10px;
        "></div>
        <div style="
          background-color: ${color};
          width: ${size}px;
          height: ${size}px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 3px 8px rgba(0,0,0,0.5);
          opacity: 1;
          position: relative;
        "></div>
      </div>
      <style>
        @keyframes pulse {
          0% {
            transform: scale(0.8);
            opacity: 1;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
      </style>
    `,
    iconSize: [size + 6, size + 6],
    iconAnchor: [(size + 6) / 2, (size + 6) / 2],
    popupAnchor: [0, -(size / 2)],
  });
};

// Component to handle map zoom to station
function MapController({ zoomToStation, selectedGeometry, stations, userHasZoomed, setUserHasZoomed, currentZoom, setCurrentZoom, zoomToCaliforniaTrigger }) {
  const map = useMap();
  const isFirstLoad = useRef(true);
  const prevStationsRef = useRef([]);
  const isUserInteractingRef = useRef(false);
  const lastPanTargetRef = useRef(null);
  const lastInteractionAtRef = useRef(0);
  const prevZoomTriggerRef = useRef(zoomToCaliforniaTrigger);
  
  // Track when user manually changes zoom
  useEffect(() => {
    const handleZoomEnd = () => {
      const newZoom = map.getZoom();
      setCurrentZoom(newZoom);
      // Mark that user has manually zoomed
      setUserHasZoomed(true);
      lastInteractionAtRef.current = Date.now();
    };
    
    map.on('zoomend', handleZoomEnd);
    
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, setUserHasZoomed, setCurrentZoom]);
  
  // Track user panning/moving the map to avoid fighting their interaction
  useEffect(() => {
    const onMoveStart = () => {
      isUserInteractingRef.current = true;
    };
    const onMoveEnd = () => {
      // small timeout to ensure any cascading rerenders finish before re-enabling autopan
      setTimeout(() => {
        isUserInteractingRef.current = false;
      }, 50);
      lastInteractionAtRef.current = Date.now();
    };

    map.on('movestart', onMoveStart);
    map.on('moveend', onMoveEnd);
    map.on('dragstart', onMoveStart);
    map.on('dragend', onMoveEnd);
    map.on('zoomstart', onMoveStart);
    map.on('zoomend', onMoveEnd);

    return () => {
      map.off('movestart', onMoveStart);
      map.off('moveend', onMoveEnd);
      map.off('dragstart', onMoveStart);
      map.off('dragend', onMoveEnd);
      map.off('zoomstart', onMoveStart);
      map.off('zoomend', onMoveEnd);
    };
  }, [map]);
  
  // Only zoom to California on initial load or county/region change
  useEffect(() => {
    if (stations && stations.length > 0) {
      // Check if this is a significant change (different county/region)
      const prevLength = prevStationsRef.current.length;
      const isNewCounty = prevLength === 0 || 
                         prevLength !== stations.length ||
                         (prevStationsRef.current[0]?.stationid !== stations[0]?.stationid);
      
      if (isNewCounty && !userHasZoomed) {
        // Only zoom out to California if user hasn't manually zoomed yet
        setTimeout(() => {
          const californiaBounds = L.latLngBounds(
            [[32.5, -124.5], [42, -114]]  // Southwest to Northeast corners of California
          );
          map.fitBounds(californiaBounds, { padding: [20, 20] });
        }, 100);
      }
      
      prevStationsRef.current = stations;
    }
  }, [stations, map, userHasZoomed]);

  // Zoom to California when trigger changes (region/county/tab switch)
  useEffect(() => {
    if (zoomToCaliforniaTrigger !== null && zoomToCaliforniaTrigger !== prevZoomTriggerRef.current) {
      // Reset user zoom flag and zoom to California
      setUserHasZoomed(false);
      setTimeout(() => {
        const californiaBounds = L.latLngBounds(
          [[32.5, -124.5], [42, -114]]  // Southwest to Northeast corners of California
        );
        map.fitBounds(californiaBounds, { padding: [20, 20] });
      }, 100);
      
      prevZoomTriggerRef.current = zoomToCaliforniaTrigger;
    }
  }, [zoomToCaliforniaTrigger, map, setUserHasZoomed]);
  
  // Only pan when explicitly requested via zoomToStation
  useEffect(() => {
    if (zoomToStation && zoomToStation.latitude && zoomToStation.longitude) {
      // Don't fight the user while they're panning the map
      if (isUserInteractingRef.current) {
        return;
      }
      // Brief cooldown after any user interaction (pan/zoom)
      if (Date.now() - lastInteractionAtRef.current < 250) {
        return;
      }
      
      // Pan to the location
      const lat = Number(zoomToStation.latitude);
      const lng = Number(zoomToStation.longitude);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        // Skip if target hasn't changed (prevents repeated recentering)
        const prev = lastPanTargetRef.current;
        if (prev && Math.abs(prev.lat - lat) < 1e-7 && Math.abs(prev.lng - lng) < 1e-7) {
          return;
        }
        lastPanTargetRef.current = { lat, lng };
        
        // Use setView to move and zoom in to the station (no animation for performance)
        const targetZoom = 14; // Zoom level for station detail view
        map.setView([lat, lng], targetZoom, {
          animate: false
        });

        // Reset user zoom flag immediately since there's no animation
        setUserHasZoomed(false);
      } else {
        console.log('MapController: Invalid coordinates:', { lat, lng });
      }
    }
  }, [zoomToStation, map, setUserHasZoomed]);

  // Only show the geometry polygons, don't auto-pan based on selectedGeometry
  // The panning is handled by zoomToStation which is triggered from both dropdown and map clicks

  // Handle map resizing
  useEffect(() => {
    const resizeMap = () => {
      map.invalidateSize();
    };

    // Initial resize
    resizeMap();
    
    // Delayed resize to handle layout settling
    const timer1 = setTimeout(resizeMap, 100);
    const timer2 = setTimeout(resizeMap, 500);
    
    // Listen for window resize
    window.addEventListener('resize', resizeMap);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      window.removeEventListener('resize', resizeMap);
    };
  }, [map]);

  return null;
}

const CaliforniaMap = ({ stations = [], onStationClick, zoomToStation, selectedGeometry, isLoadingGeometry = false, selectedStationId = null, zoomToCaliforniaTrigger = null }) => {
  // stations now contains masterid and site_geometry for all stations
  const mapRef = useRef(null);
  const mapConfig = configServices.getMapConfig();
  const [baseLayer, setBaseLayer] = useState('topography');
  const [statusType, setStatusType] = useState('reference'); // 'reference' or 'flow'
  const [userHasZoomed, setUserHasZoomed] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(null);
  const statusIconCacheRef = useRef({});
  const selectedStatusIconCacheRef = useRef({});

  const getIconForStatus = (status) => {
    const key = `${statusType}-${(status ? String(status).toLowerCase() : 'default')}`;
    if (!statusIconCacheRef.current[key]) {
      statusIconCacheRef.current[key] = createStatusIcon(status, statusType);
    }
    return statusIconCacheRef.current[key];
  };

  const getSelectedIconForStatus = (status) => {
    const key = `${statusType}-${(status ? String(status).toLowerCase() : 'default')}`;
    if (!selectedStatusIconCacheRef.current[key]) {
      selectedStatusIconCacheRef.current[key] = createSelectedStatusIcon(status, statusType);
    }
    return selectedStatusIconCacheRef.current[key];
  };

  // Check if station has recent data
  const hasRecentData = (station) => {
    const status = station.status || dataServices.getStationStatus(station);
    return status === 'active';
  };

  // Get latest result for a station
  const getLatestResult = (station) => {
    if (!station.data || station.data.length === 0) return null;
    return station.data[0].result;
  };

  // Get date of latest sample
  const getLatestDate = (station) => {
    if (!station.data || station.data.length === 0) return null;
    return new Date(station.data[0].sampledate).toLocaleDateString();
  };

  const handleBaseLayerChange = (event, newLayer) => {
    if (newLayer !== null) {
      setBaseLayer(newLayer);
    }
  };

  const handleStatusTypeChange = (event, newStatusType) => {
    if (newStatusType !== null) {
      setStatusType(newStatusType);
      // Clear icon caches when status type changes
      statusIconCacheRef.current = {};
      selectedStatusIconCacheRef.current = {};
    }
  };

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      {/* Loading Overlay */}
      {isLoadingGeometry && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            pointerEvents: 'none'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2, color: 'text.primary' }}>
              Loading data...
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Base Layer Toggle Button */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 80,
          right: 10,
          zIndex: 1000,
          backgroundColor: 'white'
        }}
      >
        <ToggleButtonGroup
          value={baseLayer}
          exclusive
          onChange={handleBaseLayerChange}
          aria-label="base layer"
          size="small"
        >
          <ToggleButton value="topography" aria-label="topography">
            Topography
          </ToggleButton>
          <ToggleButton value="aerial" aria-label="aerial">
            Aerial
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Status Type Toggle Button */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 130,
          right: 10,
          zIndex: 1000,
          backgroundColor: 'white'
        }}
      >
        <ToggleButtonGroup
          value={statusType}
          exclusive
          onChange={handleStatusTypeChange}
          aria-label="status type"
          size="small"
        >
          <ToggleButton value="reference" aria-label="reference status">
            Ref Status
          </ToggleButton>
          <ToggleButton value="flow" aria-label="flow status">
            Flow Status
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      <MapContainer
        ref={mapRef}
        center={mapConfig.defaultCenter} // Santa Monica
        zoom={mapConfig.defaultZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        dragging={true}
      >
        {/* Topography Layer (Esri Topographic) */}
        {baseLayer === 'topography' && (
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), and the GIS User Community'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
          />
        )}
        
        {/* Aerial Imagery Layer (Esri World Imagery) */}
        {baseLayer === 'aerial' && (
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        )}
        
        <MapController 
          zoomToStation={zoomToStation} 
          selectedGeometry={selectedGeometry} 
          stations={stations}
          userHasZoomed={userHasZoomed}
          setUserHasZoomed={setUserHasZoomed}
          currentZoom={currentZoom}
          setCurrentZoom={setCurrentZoom}
          zoomToCaliforniaTrigger={zoomToCaliforniaTrigger}
        />
        
        {/* Display catchment polygon if selected */}
        {selectedGeometry && selectedGeometry.catchment_geometry && (
          <GeoJSON
            key={`catchment-${selectedGeometry.masterid}-${statusType}-${((statusType === 'reference' ? selectedGeometry.refstatus_final : selectedGeometry.flowstatus_final) || 'default').toString().toLowerCase()}`}
            data={selectedGeometry.catchment_geometry}
            style={() => {
              const status = statusType === 'reference' ? selectedGeometry.refstatus_final : selectedGeometry.flowstatus_final;
              const c = statusType === 'reference' ? getStatusColor(status) : getFlowStatusColor(status);
              return {
                fillColor: c,
                fillOpacity: 0.3,
                color: c,
                weight: 2
              };
            }}
          />
        )}
        
        
        {/* Display all station points from site_geometry */}
        {stations.map((station, index) => {
          if (station.site_geometry && station.site_geometry.type === 'Point') {
            // GeoJSON uses [longitude, latitude] order
            const [lng, lat] = station.site_geometry.coordinates;
            // Check if selected station is in this location (could be any of the grouped stations)
            const isSelected = selectedStationId && (
              // Check if this location contains the selected station
              station.stationids 
                ? (Array.isArray(station.stationids) 
                    ? station.stationids.includes(selectedStationId)
                    : (typeof station.stationids === 'string' 
                        ? station.stationids.split(',').map(id => id.trim()).includes(selectedStationId)
                        : station.stationids === selectedStationId))
                : (selectedStationId === station.stationid || selectedStationId === station.masterid)
            );
            
            // Get the appropriate status field based on statusType
            const status = statusType === 'reference' ? station.refstatus_final : station.flowstatus_final;
            
            return (
              <Marker
                key={`station-${station.masterid || station.stationid || index}-${statusType}`}
                position={[lat, lng]}
                icon={isSelected ? getSelectedIconForStatus(status) : getIconForStatus(status)}
                eventHandlers={{
                  click: () => {
                    if (onStationClick) {
                      onStationClick(station);
                    }
                  }
                }}
              />
            );
          }
          return null;
        })}
      </MapContainer>

      {/* Legend - bottom left */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          bottom: 10,
          left: 10,
          zIndex: 1000,
          p: 1.5,
          backgroundColor: 'rgba(255,255,255,0.9)'
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          {statusType === 'reference' ? 'Reference Status' : 'Flow Status'}
        </Typography>
        {(statusType === 'reference' ? [
          { label: 'Reference', value: 'reference' },
          { label: 'Intermediate', value: 'intermediate' },
          { label: 'Stressed', value: 'stressed' },
          { label: 'Unknown', value: null }
        ] : [
          { label: 'P', value: 'P' },
          { label: 'NP', value: 'NP' },
          { label: 'I', value: 'I' },
          { label: 'RFI', value: 'RFI' },
          { label: 'SFI', value: 'SFI' },
          { label: 'E', value: 'E' },
          { label: 'Unknown', value: null }
        ]).map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <span
              style={{
                display: 'inline-block',
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: statusType === 'reference' ? getStatusColor(item.value) : getFlowStatusColor(item.value),
                border: '1px solid rgba(0,0,0,0.2)',
                marginRight: 8
              }}
            />
            <Typography variant="caption">{item.label}</Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default CaliforniaMap;